/**
 * ColourMatch Pro · Presentation Studio (Milestone 2 · 2026-02-09)
 *
 * The signature painter surface. Lives inside the Consultation's
 * "Presentation" tab. Every design decision here is oriented toward
 * one goal: when the painter opens this, they should feel they are
 * ASSEMBLING SOMETHING PREMIUM — not filling in another form.
 *
 * Layout (desktop):
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │  Top bar — theme · template · preview · present · download · send │
 *   ├───────────┬──────────────────────────────┬───────────────────────┤
 *   │  Section  │  Live theme-accurate canvas  │  Context-aware        │
 *   │  rail     │  (Modern renderer preview)   │  inspector            │
 *   └───────────┴──────────────────────────────┴───────────────────────┘
 *
 * The Present button flips into full-screen Keynote mode — one slide per
 * section, no toolbars, arrow keys to advance. Different from Preview
 * (which is the shareable homeowner URL opened in a new tab).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Save,
  Download,
  Send,
  Sparkles,
  Palette,
  Home,
  Camera,
  Presentation as PresentationIcon,
  ClipboardList,
  Users,
  Quote,
  Shield,
  Wrench,
  Phone,
  ArrowRightCircle,
  MoveVertical,
  Eye,
  EyeOff,
  ChevronUp,
  ChevronDown,
  Maximize2,
  X,
  Copy,
  BookmarkPlus,
  Layers,
  RotateCcw,
  Loader2,
  Check,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

// ---------------------------------------------------------------------------
// Section catalogue — mirrors backend `ALL_SECTION_KEYS`.
// ---------------------------------------------------------------------------
const SECTIONS = [
  { key: "cover",           label: "Cover",              icon: Sparkles,        kind: "dynamic",  editable: ["title", "cover_photo_url", "cover_note"] },
  { key: "property",        label: "Property snapshot",  icon: Home,            kind: "dynamic",  editable: [] },
  { key: "company_intro",   label: "Company intro",      icon: Layers,          kind: "static",   editable: ["markdown"] },
  { key: "concepts",        label: "Colour Concepts",    icon: Palette,         kind: "dynamic",  editable: ["concept_order", "included_concept_ids"] },
  { key: "before_after",    label: "Before / After",     icon: Camera,          kind: "dynamic",  editable: [] },
  { key: "designer_notes",  label: "Designer notes",     icon: Sparkles,        kind: "static",   editable: ["designer_notes_md"] },
  { key: "colour_schedule", label: "Colour Schedule",    icon: ClipboardList,   kind: "dynamic",  editable: [] },
  { key: "recommended_palette", label: "Recommended Palette", icon: Palette,   kind: "dynamic",  editable: [] },
  { key: "scope_of_works",  label: "Scope of works",     icon: ClipboardList,   kind: "static",   editable: ["markdown"] },
  { key: "investment",      label: "Investment",         icon: Layers,          kind: "static",   editable: ["markdown", "investment"] },
  { key: "about_us",        label: "About us",           icon: Users,           kind: "static",   editable: ["markdown"] },
  { key: "team",            label: "Team",               icon: Users,           kind: "static",   editable: ["items"] },
  { key: "testimonials",    label: "Testimonials",       icon: Quote,           kind: "static",   editable: ["items"] },
  { key: "warranty",        label: "Warranty",           icon: Shield,          kind: "static",   editable: ["markdown"] },
  { key: "maintenance",     label: "Care & Maintenance", icon: Wrench,          kind: "static",   editable: ["markdown"] },
  { key: "contact",         label: "Contact",            icon: Phone,           kind: "static",   editable: ["markdown"] },
  { key: "terms",           label: "Terms & conditions", icon: Shield,          kind: "static",   editable: ["markdown"] },
  { key: "acceptance",      label: "Acceptance",         icon: ClipboardList,   kind: "static",   editable: ["markdown"] },
  { key: "next_steps",      label: "Next steps",         icon: ArrowRightCircle,kind: "static",   editable: ["next_steps_md"] },
];
const SECTION_BY_KEY = SECTIONS.reduce((acc, s) => { acc[s.key] = s; return acc; }, {});

// Debounce helper so autosave feels smooth.
function useDebouncedCallback(fn, delay = 800) {
  const t = useRef();
  return useCallback((...args) => {
    if (t.current) clearTimeout(t.current);
    t.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
}

// ---------------------------------------------------------------------------
// Top-level builder
// ---------------------------------------------------------------------------
export default function PresentationBuilder({ consultationId, project, onBackToTeaser }) {
  const [presentation, setPresentation] = useState(null);
  const [themes, setThemes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedSection, setSelectedSection] = useState("cover");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [presentMode, setPresentMode] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [applyTemplateOpen, setApplyTemplateOpen] = useState(false);

  // ---- Data loaders ----
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/pro/cms/consultations/${consultationId}/presentation`);
      setPresentation(data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setPresentation(null);
      } else {
        toast.error(formatApiError(err?.response?.data?.detail) || "Could not load presentation.");
      }
    }
  }, [consultationId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/pro/cms/presentation-themes").then((r) => setThemes(r.data.items || [])).catch(() => setThemes([]));
    api.get("/pro/cms/presentation-templates").then((r) => setTemplates(r.data.items || [])).catch(() => setTemplates([]));
  }, []);

  // ---- Autosave patcher ----
  const savePatch = useCallback(async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/pro/cms/consultations/${consultationId}/presentation`, patch);
      setPresentation(data);
      setSavedAt(new Date());
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  }, [consultationId]);
  const debouncedSave = useDebouncedCallback(savePatch, 700);

  const patchLocal = (patch) => {
    setPresentation((prev) => (prev ? { ...prev, ...patch } : prev));
    debouncedSave(patch);
  };
  const patchSectionOverride = (key, override) => {
    setPresentation((prev) => {
      if (!prev) return prev;
      const next_overrides = { ...(prev.section_overrides || {}), [key]: override };
      const merged = { ...prev, section_overrides: next_overrides };
      debouncedSave({ section_overrides: next_overrides });
      return merged;
    });
  };

  // ---- Actions ----
  const create = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/pro/cms/consultations/${consultationId}/presentation`, { theme_key: "modern" });
      setPresentation(data);
      toast.success("Proposal created — start assembling your Studio.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not create presentation.");
    } finally {
      setSaving(false);
    }
  };

  const publish = async (opts = {}) => {
    setPublishing(true);
    try {
      const body = {
        notify_homeowner: !!opts.notify_homeowner,
        force_new_token: !!opts.force_new_token,
      };
      if (opts.homeowner_email) body.homeowner_email = opts.homeowner_email;
      if (opts.email_message) body.email_message = opts.email_message;
      const { data } = await api.post(`/pro/cms/consultations/${consultationId}/presentation/publish`, body);
      setPresentation(data);
      if (data?.email_sent_to) {
        toast.success(`Proposal sent · ${data.email_sent_to}`);
      } else if (opts.notify_homeowner && data?.email_skipped_reason === "no_recipient") {
        toast.info("Proposal published · add a homeowner email to send it.");
      } else if (opts.notify_homeowner && data?.email_skipped_reason === "delivery_failed") {
        toast.warning("Proposal published · email failed to send. Share the link manually.");
      } else {
        toast.success("Proposal published · share URL ready.");
      }
      setSendOpen(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Publish failed.");
    } finally {
      setPublishing(false);
    }
  };

  const downloadPdf = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/pro/cms/consultations/${consultationId}/presentation/pdf`;
    api.get(`/pro/cms/consultations/${consultationId}/presentation/pdf`, { responseType: "blob" })
      .then((r) => {
        const blob = new Blob([r.data], { type: "application/pdf" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${(presentation?.title || "presentation").replace(/[^a-z0-9-_ ]/gi, "").trim().replace(/\s+/g, "-").toLowerCase()}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch(() => toast.error("Could not download PDF."));
  };

  const previewPublic = () => {
    if (!presentation?.share_url) {
      toast.info("Publish first to get a shareable link.");
      return;
    }
    window.open(presentation.share_url, "_blank");
  };

  // ---- No presentation yet — invite to create ----
  if (presentation === null) {
    return (
      <div className="border border-brand-linen bg-white p-10 lg:p-14 text-center" data-testid="studio-empty">
        <div className="w-14 h-14 rounded-full bg-brand-teal/10 text-brand-teal flex items-center justify-center mx-auto mb-4">
          <PresentationIcon className="w-6 h-6" />
        </div>
        <h3 className="font-serif text-2xl text-brand-ink mb-2">Ready to assemble your proposal?</h3>
        <p className="text-sm text-brand-muted max-w-md mx-auto mb-6">
          Presentation Studio pulls your colour concepts, palettes and painter branding
          into one premium document — ready to share with your homeowner in minutes.
        </p>
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" onClick={onBackToTeaser} data-testid="studio-back-to-teaser">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button
            onClick={create}
            disabled={saving}
            className="bg-brand-navy text-white hover:bg-brand-teal/90 h-11 px-6"
            data-testid="studio-create"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</> : <><Sparkles className="w-4 h-4 mr-2" /> Open the Proposal Studio</>}
          </Button>
        </div>
      </div>
    );
  }

  if (!presentation) {
    return (
      <div className="flex items-center gap-2 text-sm text-brand-muted py-10" data-testid="studio-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading the Proposal Studio…
      </div>
    );
  }

  const activeTheme = themes.find((t) => t.key === presentation.theme_key) || themes[0];
  const isSuperseded = presentation.status === "superseded";

  return (
    <div className="space-y-4" data-testid="presentation-studio">
      {/* Top bar */}
      <StudioTopBar
        presentation={presentation}
        themes={themes}
        templates={templates}
        saving={saving}
        savedAt={savedAt}
        activeTheme={activeTheme}
        onBack={onBackToTeaser}
        onThemeChange={(k) => patchLocal({ theme_key: k })}
        onApplyTemplate={() => setApplyTemplateOpen(true)}
        onSaveAsTemplate={() => setSaveTemplateOpen(true)}
        onPreview={previewPublic}
        onPresent={() => setPresentMode(true)}
        onDownload={downloadPdf}
        onSend={() => setSendOpen(true)}
      />

      {isSuperseded && (
        <div className="border border-amber-300 bg-amber-50 text-amber-900 text-sm px-4 py-3 flex items-center justify-between" data-testid="studio-superseded-banner">
          <div className="flex items-center gap-2">
            <RotateCcw className="w-4 h-4" />
            You&apos;ve edited this proposal since sending it. Republish to send the updated version.
          </div>
          <Button size="sm" onClick={publish} disabled={publishing} className="bg-brand-navy text-white hover:bg-brand-teal/90" data-testid="studio-republish">
            {publishing ? "Publishing…" : "Republish"}
          </Button>
        </div>
      )}

      {/* Body */}
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr_320px] gap-4 min-h-[70vh]">
        <SectionRail
          included={presentation.included_sections || []}
          selected={selectedSection}
          onSelect={setSelectedSection}
          onChange={(next) => patchLocal({ included_sections: next })}
        />
        <CanvasPreview
          presentation={presentation}
          selectedSection={selectedSection}
          theme={activeTheme}
          project={project}
        />
        <Inspector
          presentation={presentation}
          selectedSection={selectedSection}
          onPatch={patchLocal}
          onPatchOverride={patchSectionOverride}
        />
      </div>

      {presentMode && (
        <PresentMode
          presentation={presentation}
          theme={activeTheme}
          project={project}
          onClose={() => setPresentMode(false)}
        />
      )}
      {sendOpen && (
        <SendDialog
          presentation={presentation}
          project={project}
          publishing={publishing}
          onClose={() => setSendOpen(false)}
          onPublish={publish}
        />
      )}
      {saveTemplateOpen && (
        <SaveAsTemplateDialog
          consultationId={consultationId}
          onClose={() => setSaveTemplateOpen(false)}
          onSaved={(t) => {
            setTemplates((prev) => [t, ...prev]);
            setSaveTemplateOpen(false);
          }}
        />
      )}
      {applyTemplateOpen && (
        <ApplyTemplateDialog
          consultationId={consultationId}
          templates={templates}
          onClose={() => setApplyTemplateOpen(false)}
          onApplied={(next) => { setPresentation(next); setApplyTemplateOpen(false); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top bar
// ---------------------------------------------------------------------------
function StudioTopBar({ presentation, themes, templates, saving, savedAt, activeTheme, onBack, onThemeChange, onApplyTemplate, onSaveAsTemplate, onPreview, onPresent, onDownload, onSend }) {
  const [themeOpen, setThemeOpen] = useState(false);
  const isPublished = presentation.status === "sent" || presentation.status === "viewed" || presentation.status === "accepted";
  return (
    <div className="flex flex-wrap items-center gap-2 border border-brand-linen bg-white rounded-xl px-3 py-2 shadow-soft" data-testid="studio-topbar">
      {/* Left group — navigation + document controls */}
      <div className="flex items-center gap-2">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 h-9 px-2.5 text-xs text-brand-muted hover:text-brand-ink hover:bg-brand-linen/40 rounded-lg transition-colors"
          data-testid="studio-topbar-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Studio home
        </button>
        <div className="h-6 w-px bg-brand-linen" />
        <ThemeMenu
          themes={themes}
          activeTheme={activeTheme}
          open={themeOpen}
          onOpenChange={setThemeOpen}
          onSelect={(key) => { onThemeChange(key); setThemeOpen(false); }}
        />
        <button
          onClick={onApplyTemplate}
          data-testid="studio-topbar-template"
          className="inline-flex items-center gap-1.5 h-9 px-3 text-xs text-brand-ink border border-brand-linen bg-white rounded-lg hover:border-brand-teal hover:bg-brand-linen/20 transition-colors"
        >
          <Layers className="w-3.5 h-3.5" /> Templates
          {templates.length > 0 && <span className="text-brand-muted">({templates.length})</span>}
        </button>
        <button
          onClick={onSaveAsTemplate}
          data-testid="studio-topbar-save-template"
          className="inline-flex items-center gap-1.5 h-9 px-3 text-xs text-brand-ink border border-brand-linen bg-white rounded-lg hover:border-brand-teal hover:bg-brand-linen/20 transition-colors"
        >
          <BookmarkPlus className="w-3.5 h-3.5" /> Save as template
        </button>
      </div>

      {/* Autosave status pill — subtle, centered filler */}
      <div className="flex-1 flex items-center justify-center min-w-[100px] text-[11px]">
        {saving ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 h-6 rounded-full bg-brand-linen/60 text-brand-muted">
            <Loader2 className="w-3 h-3 animate-spin" /> Saving…
          </span>
        ) : savedAt ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 h-6 rounded-full bg-brand-teal/10 text-brand-teal">
            <Check className="w-3 h-3" /> All changes saved
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2.5 h-6 rounded-full bg-brand-linen/40 text-brand-muted">
            Auto-saves as you edit
          </span>
        )}
      </div>

      {/* Right group — preview / present / send */}
      <div className="flex items-center gap-2">
        <button
          onClick={onPreview}
          disabled={!presentation.share_url}
          title={presentation.share_url ? "Preview as homeowner (opens shared URL)" : "Publish first to get a shareable link"}
          className="inline-flex items-center gap-1.5 h-9 px-3 text-xs text-brand-ink border border-brand-linen bg-white rounded-lg hover:border-brand-teal hover:bg-brand-linen/20 transition-colors disabled:opacity-40 disabled:hover:border-brand-linen"
          data-testid="studio-topbar-preview"
        >
          <Eye className="w-3.5 h-3.5" /> Preview
        </button>
        <button
          onClick={onDownload}
          className="inline-flex items-center gap-1.5 h-9 px-3 text-xs text-brand-ink border border-brand-linen bg-white rounded-lg hover:border-brand-teal hover:bg-brand-linen/20 transition-colors"
          data-testid="studio-topbar-download"
        >
          <Download className="w-3.5 h-3.5" /> PDF
        </button>
        <button
          onClick={onPresent}
          className="inline-flex items-center gap-1.5 h-9 px-3 text-xs text-white bg-brand-ink rounded-lg hover:bg-brand-navy transition-colors"
          data-testid="studio-topbar-present"
        >
          <Maximize2 className="w-3.5 h-3.5" /> Present
        </button>
        <div className="h-6 w-px bg-brand-linen" />
        <button
          onClick={onSend}
          className="inline-flex items-center gap-1.5 h-9 px-4 text-xs font-semibold text-white bg-brand-teal rounded-lg hover:bg-brand-teal/90 shadow-soft transition-colors"
          data-testid="studio-topbar-send"
        >
          <Send className="w-3.5 h-3.5" /> {isPublished ? "Republish & resend" : "Send to client"}
        </button>
      </div>
    </div>
  );
}

function ThemeMenu({ themes, activeTheme, open, onOpenChange, onSelect }) {
  return (
    <div className="relative">
      <button
        onClick={() => onOpenChange(!open)}
        data-testid="studio-topbar-theme"
        className="inline-flex items-center gap-2 text-xs text-brand-ink border border-brand-linen bg-white px-3 h-9 hover:border-brand-teal transition-colors"
      >
        <span className="w-4 h-4 border border-black/10" style={{ background: activeTheme?.preview?.gradient || "#1F3D47" }} />
        Theme · <b>{activeTheme?.name || "Modern"}</b>
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          className="absolute z-40 top-full mt-2 left-0 w-[520px] bg-white border border-brand-linen shadow-2xl p-4 grid grid-cols-2 gap-3"
          data-testid="studio-theme-menu"
        >
          {themes.map((t) => (
            <button
              key={t.key}
              onClick={() => t.available && onSelect(t.key)}
              disabled={!t.available}
              className={`text-left border transition-all ${activeTheme?.key === t.key ? "border-brand-teal ring-2 ring-brand-teal/30" : "border-brand-linen hover:border-brand-teal/60"} ${!t.available ? "opacity-90 cursor-not-allowed" : "cursor-pointer"}`}
              data-testid={`studio-theme-card-${t.key}`}
            >
              <div className="aspect-[16/9] relative overflow-hidden" style={{ background: t.preview?.gradient || "#1F3D47" }}>
                <div className="absolute inset-0" style={{
                  backgroundImage: "radial-gradient(circle at 80% 20%, rgba(255,255,255,0.28) 0%, transparent 55%)",
                }} />
                {!t.available && (
                  <div className="absolute top-2 right-2 text-[9px] uppercase tracking-widest px-1.5 h-4 flex items-center bg-black/40 text-white backdrop-blur-sm">
                    Coming soon
                  </div>
                )}
                {activeTheme?.key === t.key && t.available && (
                  <div className="absolute top-2 left-2 text-[9px] uppercase tracking-widest px-1.5 h-4 flex items-center bg-brand-teal text-white">
                    Selected
                  </div>
                )}
                <div className="absolute inset-0 flex items-end p-3">
                  <div className="text-white">
                    <div className="text-[9px] uppercase tracking-widest opacity-70">Theme preview</div>
                    <div className="font-serif text-base leading-tight" style={{ color: t.preview?.text_hex || "#fff" }}>{t.name}</div>
                  </div>
                </div>
              </div>
              <div className="px-3 py-2">
                <div className="text-[11px] text-brand-muted leading-snug">{t.tagline}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section rail (left)
// ---------------------------------------------------------------------------
function SectionRail({ included, selected, onSelect, onChange }) {
  const move = (key, dir) => {
    const idx = included.indexOf(key);
    if (idx < 0) return;
    const next = [...included];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  const toggle = (key) => {
    const next = included.includes(key)
      ? included.filter((k) => k !== key)
      : [...included, key];
    onChange(next);
  };
  const availableToAdd = SECTIONS.filter((s) => !included.includes(s.key));

  return (
    <aside className="space-y-3" data-testid="studio-section-rail">
      <div className="px-1">
        <div className="overline text-brand-muted">Sections</div>
        <div className="text-[11px] text-brand-muted/80 mt-0.5">Click a section to edit · drag on hover to reorder</div>
      </div>
      <div className="space-y-1">
        {included.map((key, i) => {
          const meta = SECTION_BY_KEY[key];
          if (!meta) return null;
          const Icon = meta.icon;
          const isActive = selected === key;
          return (
            <div
              key={key}
              className={`group flex items-center gap-2 border rounded-lg transition-colors ${isActive ? "border-brand-teal bg-brand-teal/8 shadow-soft" : "border-brand-linen bg-white hover:border-brand-teal/50 hover:bg-brand-linen/30"}`}
              data-testid={`studio-section-${key}`}
            >
              <button
                onClick={() => onSelect(key)}
                className="flex-1 flex items-center gap-2.5 text-left min-w-0 px-3 py-2.5"
              >
                <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-brand-teal" : "text-brand-muted group-hover:text-brand-ink"}`} />
                <span className={`text-xs truncate ${isActive ? "text-brand-ink font-medium" : "text-brand-ink"}`}>{meta.label}</span>
              </button>
              <div className="flex items-center gap-0.5 pr-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => move(key, -1)} title="Move up" className="text-brand-muted hover:text-brand-ink p-1 rounded hover:bg-white" data-testid={`studio-section-up-${key}`}>
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => move(key, 1)} title="Move down" className="text-brand-muted hover:text-brand-ink p-1 rounded hover:bg-white" data-testid={`studio-section-down-${key}`}>
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => toggle(key)} title="Remove section" className="text-brand-muted hover:text-red-600 p-1 rounded hover:bg-white" data-testid={`studio-section-toggle-${key}`}>
                  <EyeOff className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {availableToAdd.length > 0 && (
        <div className="pt-4 border-t border-brand-linen">
          <div className="overline text-brand-muted px-1 mb-2">Add section</div>
          <div className="space-y-1">
            {availableToAdd.map((s) => {
              const Icon = s.icon;
              return (
                <button
                  key={s.key}
                  onClick={() => { toggle(s.key); onSelect(s.key); }}
                  className="w-full flex items-center gap-2.5 text-left border border-dashed border-brand-linen rounded-lg px-3 py-2 text-xs text-brand-muted hover:text-brand-ink hover:border-brand-teal hover:bg-brand-linen/20 transition-colors"
                  data-testid={`studio-section-add-${s.key}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="flex-1 truncate">{s.label}</span>
                  <span className="text-[10px] uppercase tracking-widest text-brand-teal/80 font-semibold">+ Add</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Canvas preview (center) — client-side theme renderer that mirrors PDF
// ---------------------------------------------------------------------------
function CanvasPreview({ presentation, selectedSection, theme, project }) {
  return (
    <div className="border border-brand-linen bg-brand-linen/40 rounded-xl overflow-hidden shadow-inner" data-testid="studio-canvas">
      <div className="px-6 py-2.5 border-b border-brand-linen bg-white/50 flex items-center justify-between">
        <div className="overline text-brand-muted">Live preview</div>
        <div className="text-[10px] uppercase tracking-widest text-brand-muted/70">Modern theme</div>
      </div>
      <div className="p-5 lg:p-8 overflow-y-auto max-h-[calc(100vh-260px)]">
        <div className="mx-auto max-w-2xl space-y-4">
          {(presentation.included_sections || []).map((key) => (
            <SectionSlide
              key={key}
              sectionKey={key}
              presentation={presentation}
              theme={theme}
              project={project}
              isActive={selectedSection === key}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function SectionSlide({ sectionKey, presentation, theme, project, isActive }) {
  const grad = theme?.preview?.gradient || "linear-gradient(135deg, #1F3D47 0%, #2EB3A0 100%)";
  const accent = theme?.preview?.accent_hex || "#2EB3A0";
  const painter = presentation.branding_snapshot?.business_name || "Your Painting Business";
  const client = project?.client_name || "Prepared for you";
  const site = project?.site_address || "";
  const overrides = presentation.section_overrides || {};

  const ring = isActive ? "ring-2 ring-brand-teal" : "";
  const testId = `studio-slide-${sectionKey}`;

  if (sectionKey === "cover") {
    return (
      <div className={`relative aspect-[16/10] text-white overflow-hidden ${ring}`} style={{ background: grad }} data-testid={testId}>
        <div className="absolute inset-0" style={{
          backgroundImage: "linear-gradient(115deg, rgba(255,255,255,0.14) 0%, transparent 45%)",
        }} />
        <div className="relative p-8 h-full flex flex-col justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] opacity-80">Client Presentation</div>
            <div className="w-8 h-0.5 mt-2 mb-4" style={{ background: accent }} />
            <div className="font-serif text-3xl leading-tight" style={{ color: theme?.preview?.text_hex || "#fff" }}>
              {presentation.title || `${client} · Visualisation Proposal`}
            </div>
            {site && <div className="text-white/75 text-sm mt-2">{site}</div>}
            {presentation.cover_note && (
              <div className="mt-4 max-w-md text-sm italic text-white/85">
                &ldquo;{presentation.cover_note}&rdquo;
              </div>
            )}
          </div>
          <div className="flex items-end justify-between">
            <div>
              <div className="text-[9px] uppercase tracking-wider text-white/60">Prepared by</div>
              <div className="font-medium text-base">{painter}</div>
            </div>
            <div className="text-[9px] uppercase tracking-wider text-white/60">
              {new Date().toLocaleDateString("en-AU", { month: "long", year: "numeric" })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (sectionKey === "property") {
    return (
      <SlideChrome ring={ring} testId={testId} kicker="Property snapshot" title="The Property" accent={accent}>
        <dl className="grid grid-cols-1 gap-y-2 text-sm">
          <SlideRow k="Client" v={project?.client_name || "—"} />
          <SlideRow k="Site" v={project?.site_address || "—"} />
          <SlideRow k="Contact" v={project?.client_email || "—"} />
        </dl>
        {project?.notes && <div className="text-xs text-brand-muted mt-3 italic">{project.notes}</div>}
      </SlideChrome>
    );
  }

  if (sectionKey === "concepts") {
    const order = presentation.concept_order || [];
    const included = new Set(presentation.included_concept_ids || []);
    const activeIds = order.filter((id) => !included.size || included.has(id));
    return (
      <SlideChrome ring={ring} testId={testId} kicker="The options" title="Colour Concepts" accent={accent}>
        {activeIds.length === 0 ? (
          <div className="text-sm text-brand-muted italic">
            Add colour concepts in the Colour Concepts tab. They&apos;ll appear here as beautiful, editorial concept boards.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-brand-ink">
              <b>{activeIds.length}</b> colour concept{activeIds.length === 1 ? "" : "s"} — each one a complete direction.
            </div>
            <div className="grid grid-cols-3 gap-2">
              {activeIds.slice(0, 6).map((id, i) => {
                const letter = String.fromCharCode(65 + i);
                return (
                  <div key={id} className="border border-brand-linen p-2 text-center">
                    <div className="aspect-[4/3] mb-1" style={{ background: `linear-gradient(135deg, ${accent}22 0%, ${accent}66 100%)` }} />
                    <div className="text-[10px] uppercase tracking-widest text-brand-muted">Concept {letter}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </SlideChrome>
    );
  }

  if (sectionKey === "before_after") {
    return (
      <SlideChrome ring={ring} testId={testId} kicker="See the transformation" title="Before / After" accent={accent}>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="aspect-[4/3] bg-stone-200 relative">
            <div className="absolute top-2 left-2 text-[9px] uppercase tracking-wider text-stone-500">Before</div>
          </div>
          <div className="aspect-[4/3] relative" style={{ background: grad }}>
            <div className="absolute top-2 left-2 text-[9px] uppercase tracking-wider text-white/85">After</div>
          </div>
        </div>
      </SlideChrome>
    );
  }

  if (sectionKey === "designer_notes") {
    return (
      <SlideChrome ring={ring} testId={testId} kicker="From your painter" title="Designer Notes" accent={accent}>
        <div className="text-sm text-brand-ink whitespace-pre-wrap min-h-[3em]">
          {presentation.designer_notes_md || overrides.designer_notes?.markdown ||
            <span className="text-brand-muted italic">Your designer notes will appear here. Use the inspector on the right to write them.</span>}
        </div>
      </SlideChrome>
    );
  }

  if (sectionKey === "colour_schedule") {
    return (
      <SlideChrome ring={ring} testId={testId} kicker="Once you choose a concept" title="Colour Schedule" accent={accent}>
        <div className="text-sm text-brand-muted">
          The full schedule renders here from the selected concept — surface, colour name, library code, and hex.
        </div>
      </SlideChrome>
    );
  }

  // Painter-static sections
  const staticKeyMap = {
    company_intro:  { kicker: "Company introduction", title: "A word from your painter" },
    about_us:       { kicker: "Meet your painter",    title: "About Us" },
    team:           { kicker: "The people on your job", title: "Our Team" },
    testimonials:   { kicker: "Testimonials",         title: "What our clients say" },
    warranty:       { kicker: "Our commitment",       title: "Warranty" },
    maintenance:    { kicker: "Keep it looking new",  title: "Care & Maintenance" },
    contact:        { kicker: "Contact",              title: "Get in touch" },
    next_steps:     { kicker: "What happens now",     title: "Next Steps" },
    scope_of_works: { kicker: "Scope of works",       title: "What will be done" },
    investment:     { kicker: "Investment",           title: "Your project total" },
    terms:          { kicker: "Terms & conditions",   title: "Our agreement" },
    acceptance:     { kicker: "Acceptance",           title: "Client signature block" },
  };
  const map = staticKeyMap[sectionKey];
  if (!map) return null;
  const md = sectionKey === "next_steps"
    ? (presentation.next_steps_md || overrides.next_steps?.markdown)
    : overrides[sectionKey]?.markdown;
  const items = overrides[sectionKey]?.items || [];

  return (
    <SlideChrome ring={ring} testId={testId} kicker={map.kicker} title={map.title} accent={accent}>
      {sectionKey === "testimonials" && items.length > 0 ? (
        <div className="space-y-4">
          {items.slice(0, 4).map((t, i) => (
            <div key={i}>
              <div className="italic text-brand-ink text-base">&ldquo;{t.quote}&rdquo;</div>
              <div className="text-xs text-brand-muted mt-1">— {t.author}{t.source ? ` · ${t.source}` : ""}</div>
            </div>
          ))}
        </div>
      ) : sectionKey === "team" && items.length > 0 ? (
        <div className="space-y-2">
          {items.slice(0, 6).map((m, i) => (
            <div key={i} className="text-sm text-brand-ink">
              <b>{m.name}</b>{m.role ? <span className="text-brand-muted"> · {m.role}</span> : null}
              {m.bio && <div className="text-xs text-brand-muted mt-0.5">{m.bio}</div>}
            </div>
          ))}
        </div>
      ) : md ? (
        <div className="text-sm text-brand-ink whitespace-pre-wrap">{md}</div>
      ) : (
        <div className="text-sm text-brand-muted italic">
          {sectionKey === "contact"
            ? "Your business contact details will appear here."
            : "Add content from the inspector on the right — or apply a template to fill this in instantly."}
        </div>
      )}
    </SlideChrome>
  );
}

function SlideChrome({ children, kicker, title, accent, ring, testId }) {
  return (
    <div className={`bg-white border border-brand-linen p-8 aspect-[16/10] ${ring}`} data-testid={testId}>
      <div className="h-full flex flex-col">
        <div className="text-[9px] uppercase tracking-[0.22em]" style={{ color: accent || "#2EB3A0" }}><b>{kicker}</b></div>
        <div className="w-8 h-0.5 mt-2 mb-3" style={{ background: accent }} />
        <div className="font-serif text-2xl text-brand-ink mb-4">{title}</div>
        <div className="flex-1 overflow-hidden">{children}</div>
        <div className="pt-2 mt-2 border-t border-brand-linen flex items-center justify-between text-[9px] text-brand-muted/70">
          <span className="text-brand-ink">Generated by ColourMatch Pro</span>
          <span className="opacity-60">Preview · not for print</span>
        </div>
      </div>
    </div>
  );
}

function SlideRow({ k, v }) {
  return (
    <div className="flex items-baseline justify-between border-b border-brand-linen/60 py-1.5">
      <dt className="text-xs uppercase tracking-widest text-brand-muted">{k}</dt>
      <dd className="text-sm text-brand-ink truncate ml-4">{v}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inspector (right) — context-aware editor
// ---------------------------------------------------------------------------
function Inspector({ presentation, selectedSection, onPatch, onPatchOverride }) {
  const meta = SECTION_BY_KEY[selectedSection];
  if (!meta) {
    return (
      <aside className="border border-brand-linen bg-white rounded-xl p-6 text-center shadow-soft" data-testid="studio-inspector">
        <div className="w-10 h-10 rounded-full bg-brand-linen/60 text-brand-muted flex items-center justify-center mx-auto mb-3">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="text-sm text-brand-ink font-medium mb-1">Pick a section</div>
        <div className="text-[11px] text-brand-muted leading-relaxed">
          Select a section from the left rail to edit its content here.
        </div>
      </aside>
    );
  }
  const overrides = presentation.section_overrides || {};
  const SectionIcon = meta.icon;

  return (
    <aside
      className="border border-brand-linen bg-white rounded-xl shadow-soft max-h-[calc(100vh-220px)] overflow-y-auto"
      data-testid="studio-inspector"
    >
      {/* Sticky inspector header */}
      <div className="sticky top-0 z-10 bg-white border-b border-brand-linen px-5 py-4">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${meta.kind === "dynamic" && (meta.editable || []).length === 0 ? "bg-brand-teal/10 text-brand-teal" : "bg-brand-navy/10 text-brand-navy"}`}>
            <SectionIcon className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="overline text-brand-muted mb-0.5">Editing</div>
            <div className="font-serif text-base text-brand-ink leading-tight">{meta.label}</div>
          </div>
        </div>
        <div className={`text-[11px] leading-relaxed mt-3 px-3 py-2 rounded-lg ${meta.kind === "dynamic" && (meta.editable || []).length === 0 ? "bg-brand-teal/5 text-brand-teal" : "bg-brand-linen/40 text-brand-muted"}`}>
          {meta.kind === "dynamic" && (meta.editable || []).length === 0
            ? "This section renders live from your client project data — edit the underlying data to update it."
            : meta.kind === "dynamic"
            ? "Live from your client project — you can also fine-tune the fields below."
            : "Painter-authored content. Anything you type here appears verbatim in the proposal."}
        </div>
      </div>

      <div className="p-5 space-y-4">

      {selectedSection === "cover" && (
        <div className="space-y-4">
          <FieldGroup label="Title">
            <Input
              value={presentation.title || ""}
              onChange={(e) => onPatch({ title: e.target.value })}
              data-testid="inspector-cover-title"
              maxLength={200}
            />
          </FieldGroup>
          <FieldGroup label="Cover note (optional)" hint="A short personal note to your client. Shown on the cover.">
            <Textarea
              value={presentation.cover_note || ""}
              onChange={(e) => onPatch({ cover_note: e.target.value })}
              rows={3}
              placeholder="e.g. Thank you for inviting us to your home. Here are the concepts we've prepared for you."
              data-testid="inspector-cover-note"
              maxLength={2000}
            />
          </FieldGroup>
          <FieldGroup label="Cover photo URL (optional)" hint="Paste a link to a property hero photo (jpg or png).">
            <Input
              value={presentation.cover_photo_url || ""}
              onChange={(e) => onPatch({ cover_photo_url: e.target.value })}
              placeholder="https://…"
              data-testid="inspector-cover-photo"
            />
          </FieldGroup>
        </div>
      )}

      {selectedSection === "designer_notes" && (
        <FieldGroup label="Designer notes" hint="Write in a warm, professional voice — explain why you chose these colours.">
          <Textarea
            value={presentation.designer_notes_md || ""}
            onChange={(e) => onPatch({ designer_notes_md: e.target.value })}
            rows={12}
            placeholder="Write in a warm, professional voice. Explain why you chose these colours and what feeling they will create."
            data-testid="inspector-designer-notes"
            maxLength={8000}
          />
        </FieldGroup>
      )}

      {selectedSection === "next_steps" && (
        <FieldGroup label="Next steps" hint="Tell the client what happens after they pick a concept.">
          <Textarea
            value={presentation.next_steps_md || ""}
            onChange={(e) => onPatch({ next_steps_md: e.target.value })}
            rows={8}
            placeholder="Tell the client what happens after they pick a concept — quote, timeline, deposit."
            data-testid="inspector-next-steps"
            maxLength={4000}
          />
        </FieldGroup>
      )}

      {(selectedSection === "company_intro" || selectedSection === "about_us" || selectedSection === "warranty" || selectedSection === "maintenance" || selectedSection === "contact" || selectedSection === "scope_of_works" || selectedSection === "terms" || selectedSection === "acceptance") && (
        <FieldGroup
          label={`${meta.label} · content`}
          hint={
            selectedSection === "scope_of_works" ? "Describe the prep, paint system, coats and timeline. Supports **bold** and - bullet lists." :
            selectedSection === "terms" ? "Deposit terms, payment stages, warranty. Supports **bold** and - bullets." :
            selectedSection === "acceptance" ? "Optional preamble text shown above the signature block." :
            "Blank lines create new paragraphs. Supports **bold** and - bullets."
          }
        >
          <Textarea
            value={overrides[selectedSection]?.markdown || ""}
            onChange={(e) => onPatchOverride(selectedSection, { ...(overrides[selectedSection] || {}), markdown: e.target.value })}
            rows={10}
            placeholder={
              selectedSection === "scope_of_works" ? "**Preparation**\n- Wash all exterior surfaces\n- Fill and sand rough patches" :
              selectedSection === "terms" ? "This proposal is valid for 30 days from the date of issue.\n\n- **Deposit** — 20% required to secure the booking." :
              selectedSection === "acceptance" ? "Ready to proceed? Review the colour plan, scope of works and investment above." :
              "Type here. Blank lines create new paragraphs."
            }
            data-testid={`inspector-md-${selectedSection}`}
            maxLength={8000}
          />
        </FieldGroup>
      )}

      {selectedSection === "investment" && (
        <InvestmentEditor
          override={overrides.investment || {}}
          onChange={(next) => onPatchOverride("investment", next)}
        />
      )}

      {selectedSection === "testimonials" && (
        <TestimonialsEditor
          items={overrides.testimonials?.items || []}
          onChange={(items) => onPatchOverride("testimonials", { items })}
        />
      )}

      {selectedSection === "team" && (
        <TeamEditor
          items={overrides.team?.items || []}
          onChange={(items) => onPatchOverride("team", { items })}
        />
      )}

      {selectedSection === "concepts" && (
        <ConceptsEditor
          presentation={presentation}
          onPatch={onPatch}
        />
      )}

      {(selectedSection === "property" || selectedSection === "before_after" || selectedSection === "colour_schedule" || selectedSection === "recommended_palette") && (
        <div className="text-center py-4">
          <div className="w-10 h-10 rounded-full bg-brand-teal/10 text-brand-teal flex items-center justify-center mx-auto mb-3">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="text-sm text-brand-ink font-medium mb-1">Nothing to type here</div>
          <div className="text-[11px] text-brand-muted leading-relaxed max-w-xs mx-auto">
            This section pulls live from your <b>Colour Concepts</b> and <b>Overview</b> tabs. Edit those and this section updates automatically.
          </div>
        </div>
      )}
      </div>
    </aside>
  );
}

// Small helper to make every inspector field visually consistent —
// label above · optional hint below field.
function FieldGroup({ label, hint, children }) {
  return (
    <div>
      <Label className="text-brand-ink text-[11px] font-semibold uppercase tracking-wider">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && (
        <div className="text-[11px] text-brand-muted mt-1.5 leading-snug">{hint}</div>
      )}
    </div>
  );
}

function InvestmentEditor({ override, onChange }) {
  const inv = (override?.attributes?.investment) || {};
  const items = Array.isArray(inv.line_items) ? inv.line_items : [];
  const setInv = (patch) => {
    const nextInv = { ...inv, ...patch };
    const nextAttrs = { ...(override?.attributes || {}), investment: nextInv };
    onChange({ ...(override || {}), attributes: nextAttrs });
  };
  const setItems = (nextItems) => setInv({ line_items: nextItems });
  const setNotes = (markdown) => onChange({ ...(override || {}), markdown });
  const patchItem = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const addItem = () => setItems([...items, { label: "", amount_aud: 0 }]);
  return (
    <div className="space-y-5" data-testid="inspector-investment">
      <FieldGroup label="Project total (AUD)" hint="Leave blank to show 'TBC' — never blocks publishing.">
        <Input
          type="number"
          min="0"
          step="1"
          value={inv.total_aud ?? ""}
          onChange={(e) => setInv({ total_aud: e.target.value === "" ? null : Number(e.target.value) })}
          placeholder="e.g. 12500"
          className="text-sm"
          data-testid="inspector-investment-total"
        />
      </FieldGroup>

      <div className="flex items-center gap-2 py-1 px-3 rounded-lg bg-brand-linen/40">
        <input
          id="inv-gst"
          type="checkbox"
          checked={inv.gst_inclusive !== false}
          onChange={(e) => setInv({ gst_inclusive: e.target.checked })}
          className="accent-brand-teal w-4 h-4"
          data-testid="inspector-investment-gst"
        />
        <label htmlFor="inv-gst" className="text-xs text-brand-ink cursor-pointer">
          Project total is <b>GST inclusive</b>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <FieldGroup label="Deposit %">
          <Input
            type="number"
            min="0"
            max="100"
            step="1"
            value={inv.deposit_pct ?? ""}
            onChange={(e) => setInv({ deposit_pct: e.target.value === "" ? null : Number(e.target.value) })}
            placeholder="20"
            className="text-sm"
            data-testid="inspector-investment-deposit"
          />
        </FieldGroup>
        <FieldGroup label="Valid until">
          <Input
            type="date"
            value={inv.valid_until || ""}
            onChange={(e) => setInv({ valid_until: e.target.value || null })}
            className="text-sm"
            data-testid="inspector-investment-valid-until"
          />
        </FieldGroup>
      </div>

      <div>
        <Label className="text-brand-ink text-[11px] font-semibold uppercase tracking-wider">Line items</Label>
        <div className="mt-1.5 space-y-2">
          {items.length === 0 && (
            <div className="text-[11px] text-brand-muted italic px-1">Optional — break the project into line items if it helps.</div>
          )}
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-[1fr_92px_28px] gap-2 items-center">
              <Input value={it.label || ""} onChange={(e) => patchItem(i, "label", e.target.value)} placeholder="Line description" className="text-sm h-9" />
              <Input type="number" min="0" step="1" value={it.amount_aud ?? 0} onChange={(e) => patchItem(i, "amount_aud", Number(e.target.value))} className="text-sm h-9" />
              <button onClick={() => removeItem(i)} title="Remove line" className="h-9 w-8 flex items-center justify-center text-brand-muted hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <button
            onClick={addItem}
            className="w-full flex items-center justify-center gap-1.5 border border-dashed border-brand-linen rounded-lg text-xs text-brand-muted hover:text-brand-ink hover:border-brand-teal hover:bg-brand-linen/20 py-2 transition-colors"
            data-testid="inspector-investment-add-item"
          >
            <Plus className="w-3.5 h-3.5" /> Add line item
          </button>
        </div>
      </div>

      <FieldGroup label="Notes (optional)" hint="Anything else the client should know — inclusions, exclusions, assumptions.">
        <Textarea
          value={override?.markdown || ""}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          placeholder="Includes all materials, labour, drop-sheeting, waste removal and final clean."
          maxLength={4000}
          data-testid="inspector-investment-notes"
        />
      </FieldGroup>
    </div>
  );
}

function TestimonialsEditor({ items, onChange }) {
  const add = () => onChange([...items, { quote: "", author: "", source: "" }]);
  const patch = (i, k, v) => onChange(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3" data-testid="inspector-testimonials">
      {items.length === 0 && <div className="text-xs text-brand-muted italic">No testimonials yet — add your first below.</div>}
      {items.map((it, i) => (
        <div key={i} className="border border-brand-linen p-3 space-y-2">
          <Textarea value={it.quote || ""} onChange={(e) => patch(i, "quote", e.target.value)} rows={2} placeholder="Quote" className="text-sm" />
          <Input value={it.author || ""} onChange={(e) => patch(i, "author", e.target.value)} placeholder="Author" className="text-sm" />
          <Input value={it.source || ""} onChange={(e) => patch(i, "source", e.target.value)} placeholder="Source (e.g. suburb)" className="text-sm" />
          <button onClick={() => remove(i)} className="text-[11px] text-red-600 hover:underline">Remove</button>
        </div>
      ))}
      <button onClick={add} className="w-full border border-dashed border-brand-linen text-xs text-brand-muted hover:text-brand-ink hover:border-brand-teal py-2 transition-colors">
        + Add testimonial
      </button>
    </div>
  );
}

function TeamEditor({ items, onChange }) {
  const add = () => onChange([...items, { name: "", role: "", bio: "" }]);
  const patch = (i, k, v) => onChange(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3" data-testid="inspector-team">
      {items.length === 0 && <div className="text-xs text-brand-muted italic">Add team members to introduce who will be on the job.</div>}
      {items.map((it, i) => (
        <div key={i} className="border border-brand-linen p-3 space-y-2">
          <Input value={it.name || ""} onChange={(e) => patch(i, "name", e.target.value)} placeholder="Name" className="text-sm" />
          <Input value={it.role || ""} onChange={(e) => patch(i, "role", e.target.value)} placeholder="Role" className="text-sm" />
          <Textarea value={it.bio || ""} onChange={(e) => patch(i, "bio", e.target.value)} rows={2} placeholder="Short bio" className="text-sm" />
          <button onClick={() => remove(i)} className="text-[11px] text-red-600 hover:underline">Remove</button>
        </div>
      ))}
      <button onClick={add} className="w-full border border-dashed border-brand-linen text-xs text-brand-muted hover:text-brand-ink hover:border-brand-teal py-2 transition-colors">
        + Add member
      </button>
    </div>
  );
}

function ConceptsEditor({ presentation, onPatch }) {
  const [concepts, setConcepts] = useState([]);
  useEffect(() => {
    api.get(`/pro/cms/projects/${presentation.consultation_id}/schemes`)
      .then((r) => setConcepts(r.data.items || []))
      .catch(() => setConcepts([]));
  }, [presentation.consultation_id]);

  const order = presentation.concept_order || [];
  const included = new Set(presentation.included_concept_ids || []);
  const heroByConcept = presentation.hero_photo_per_concept || {};

  // Merge: painter's declared order first, then any concepts that exist but
  // aren't in the presentation's order yet (newly-created after the pres).
  const ordered = [
    ...order.filter((id) => concepts.some((c) => c.id === id)),
    ...concepts.map((c) => c.id).filter((id) => !order.includes(id)),
  ];

  const patchOrder = (nextOrder) => {
    onPatch({ concept_order: nextOrder, included_concept_ids: nextOrder.filter((id) => included.has(id) || !presentation.concept_order?.length) });
  };
  const move = (id, dir) => {
    const next = [...ordered];
    const idx = next.indexOf(id);
    if (idx < 0) return;
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    patchOrder(next);
  };
  const toggle = (id) => {
    const next = new Set(included);
    if (next.has(id)) next.delete(id); else next.add(id);
    onPatch({ included_concept_ids: Array.from(next) });
  };
  const setHero = (id, url) => {
    onPatch({ hero_photo_per_concept: { ...heroByConcept, [id]: url } });
  };

  if (concepts.length === 0) {
    return (
      <div className="text-xs text-brand-muted italic border border-dashed border-brand-linen p-3">
        No colour concepts yet — head to the Colour Concepts tab to create one, then come back to arrange them here.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="inspector-concepts">
      <div className="text-[11px] text-brand-muted mb-1">
        Choose which concepts appear, in what order. Add a hero photo URL per concept to make each board unforgettable.
      </div>
      {ordered.map((id, i) => {
        const c = concepts.find((x) => x.id === id);
        if (!c) return null;
        const letter = String.fromCharCode(65 + i);
        const inc = included.size === 0 || included.has(id);
        return (
          <div key={id} className={`border p-2.5 ${inc ? "border-brand-linen bg-white" : "border-brand-linen/50 bg-brand-linen/20 opacity-70"}`}>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-brand-teal">Concept {letter}</span>
              <div className="flex-1 min-w-0 font-medium text-sm text-brand-ink truncate">{c.name}</div>
              <div className="flex items-center gap-1">
                <button onClick={() => move(id, -1)} title="Move up" className="p-0.5 text-brand-muted hover:text-brand-ink"><ChevronUp className="w-3 h-3" /></button>
                <button onClick={() => move(id, 1)} title="Move down" className="p-0.5 text-brand-muted hover:text-brand-ink"><ChevronDown className="w-3 h-3" /></button>
                <button onClick={() => toggle(id)} title={inc ? "Hide" : "Show"} className={`p-0.5 ${inc ? "text-brand-teal" : "text-brand-muted"} hover:text-brand-ink`}>
                  {inc ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                </button>
              </div>
            </div>
            <Input
              value={heroByConcept[id] || ""}
              onChange={(e) => setHero(id, e.target.value)}
              placeholder="Hero photo URL (optional)"
              className="mt-2 text-xs"
              data-testid={`inspector-concept-hero-${id}`}
            />
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Present Mode (fullscreen · Keynote-style)
// ---------------------------------------------------------------------------
function PresentMode({ presentation, theme, project, onClose }) {
  const sections = presentation.included_sections || [];
  const [idx, setIdx] = useState(0);
  const total = sections.length;

  const next = useCallback(() => setIdx((i) => Math.min(total - 1, i + 1)), [total]);
  const prev = useCallback(() => setIdx((i) => Math.max(0, i - 1)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") { e.preventDefault(); next(); }
      else if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); prev(); }
      else if (e.key === "Home") setIdx(0);
      else if (e.key === "End") setIdx(total - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, onClose, total]);

  const key = sections[idx];

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col" data-testid="studio-present-mode">
      {/* Overlay chrome auto-hides after a moment; keep simple = always visible but muted */}
      <div className="flex items-center justify-between px-6 py-3 text-white/60 text-xs">
        <div>Presentation Mode · {presentation.title}</div>
        <button onClick={onClose} className="hover:text-white inline-flex items-center gap-1.5" data-testid="present-close">
          <X className="w-4 h-4" /> Exit (Esc)
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-6xl aspect-[16/9] bg-white shadow-2xl">
          <div className="w-full h-full [&_[data-testid^='studio-slide-']]:h-full [&_[data-testid^='studio-slide-']]:aspect-auto">
            <SectionSlide sectionKey={key} presentation={presentation} theme={theme} project={project} isActive={false} />
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between px-6 py-3 text-white/60 text-xs">
        <button
          onClick={prev}
          disabled={idx === 0}
          className="inline-flex items-center gap-1.5 hover:text-white disabled:opacity-30"
          data-testid="present-prev"
        >
          <ArrowLeft className="w-4 h-4" /> Previous
        </button>
        <div>{idx + 1} / {total}</div>
        <button
          onClick={next}
          disabled={idx === total - 1}
          className="inline-flex items-center gap-1.5 hover:text-white disabled:opacity-30"
          data-testid="present-next"
        >
          Next <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialogs
// ---------------------------------------------------------------------------
function SendDialog({ presentation, project, publishing, onClose, onPublish }) {
  const share = presentation.share_url;
  const alreadySent = presentation.status === "sent" || presentation.status === "viewed";
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState(project?.client_email || "");
  const [message, setMessage] = useState("");
  const [notify, setNotify] = useState(true);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());
  const willEmail = notify && emailValid;

  const copy = async () => {
    if (!share) return;
    try {
      await navigator.clipboard.writeText(share);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy — long-press the link to copy manually.");
    }
  };

  const submit = () => {
    onPublish({
      notify_homeowner: willEmail,
      homeowner_email: willEmail ? email.trim() : undefined,
      email_message: willEmail && message.trim() ? message.trim() : undefined,
    });
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="studio-send-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-brand-ink">
            {alreadySent ? "Republish presentation" : "Send to your client"}
          </DialogTitle>
          <DialogDescription className="text-sm text-brand-muted">
            Publishing generates a shareable link and a branded PDF your homeowner can view on any device.
            Add their email below to send it directly — the PDF will be attached.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label htmlFor="send-homeowner-email" className="text-brand-ink text-xs font-semibold">
                Homeowner email
              </Label>
              <label className="inline-flex items-center gap-1.5 text-[11px] text-brand-muted cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={notify}
                  onChange={(e) => setNotify(e.target.checked)}
                  className="w-3.5 h-3.5"
                  data-testid="send-notify-toggle"
                />
                Send email now
              </label>
            </div>
            <Input
              id="send-homeowner-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="homeowner@example.com"
              autoComplete="off"
              data-testid="send-homeowner-email"
              disabled={!notify}
            />
            {notify && email.trim() && !emailValid && (
              <div className="text-[11px] text-amber-700 mt-1">
                Enter a valid email address to send.
              </div>
            )}
            {notify && !email.trim() && (
              <div className="text-[11px] text-brand-muted mt-1">
                No email yet — you can publish now and share the link manually.
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="send-message" className="text-brand-ink text-xs font-semibold">
              Personal message <span className="text-brand-muted font-normal">(optional)</span>
            </Label>
            <Textarea
              id="send-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={`e.g. Hi ${project?.client_name?.split(" ")?.[0] || "there"} — here's the colour plan we spoke about. Let me know your thoughts.`}
              maxLength={2000}
              rows={3}
              data-testid="send-message"
              disabled={!notify}
            />
            <div className="text-[11px] text-brand-muted mt-1">
              Shown at the top of the email, above the &ldquo;View your presentation&rdquo; button.
            </div>
          </div>

          {share && (
            <div className="border border-brand-linen bg-brand-linen/30 p-3 flex items-center gap-2">
              <div className="text-xs text-brand-ink truncate flex-1" data-testid="send-share-url">{share}</div>
              <button
                onClick={copy}
                className="text-xs text-brand-teal hover:underline whitespace-nowrap"
                data-testid="send-copy-link"
                type="button"
              >
                {copied ? <><Check className="w-3.5 h-3.5 inline mr-1" /> Copied</> : <><Copy className="w-3.5 h-3.5 inline mr-1" /> Copy</>}
              </button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Close</Button>
          <Button
            onClick={submit}
            disabled={publishing}
            className="bg-brand-navy text-white hover:bg-brand-teal/90"
            data-testid="send-publish"
          >
            {publishing ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {willEmail ? "Sending…" : "Publishing…"}</>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                {willEmail
                  ? (alreadySent ? "Republish & email homeowner" : "Publish & email homeowner")
                  : (share ? "Republish (link only)" : "Publish (link only)")}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SaveAsTemplateDialog({ consultationId, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/pro/cms/consultations/${consultationId}/presentation/save-as-template`, null, {
        params: { name: name.trim() },
      });
      toast.success("Template saved. Reuse it on future consultations.");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not save template.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="studio-save-template-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-brand-ink">Save as template</DialogTitle>
            <DialogDescription className="text-sm text-brand-muted">
              Templates store your painter-static sections (About Us · Team · Testimonials · Warranty · Maintenance · Contact · Next Steps).
              Consultation-specific data is never included — it always renders live.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-brand-ink text-xs">Template name *</Label>
            <Input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Standard Residential Pack"
              maxLength={140}
              data-testid="save-template-name"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy || !name.trim()} className="bg-brand-navy text-white hover:bg-brand-teal/90" data-testid="save-template-submit">
              {busy ? "Saving…" : "Save template"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ApplyTemplateDialog({ consultationId, templates, onClose, onApplied }) {
  const [busyId, setBusyId] = useState(null);
  const apply = async (t) => {
    setBusyId(t.id);
    try {
      const { data } = await api.post(`/pro/cms/presentation-templates/${t.id}/apply-to/${consultationId}`);
      toast.success(`Applied template · ${t.name}`);
      onApplied(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not apply template.");
    } finally {
      setBusyId(null);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="studio-apply-template-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-brand-ink">Apply a template</DialogTitle>
          <DialogDescription className="text-sm text-brand-muted">
            Instantly fill your painter-static sections from a saved template. Live consultation data stays untouched.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2 max-h-[60vh] overflow-y-auto">
          {templates.length === 0 && (
            <div className="text-sm text-brand-muted italic py-4">
              You haven&apos;t saved any templates yet. Save this presentation as a template to build your reusable pack.
            </div>
          )}
          {templates.map((t) => {
            const sectionKeys = Object.keys(t.sections || {});
            return (
              <div key={t.id} className="border border-brand-linen p-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-brand-ink text-sm">{t.name}</div>
                  <div className="text-[11px] text-brand-muted mt-0.5">
                    {sectionKeys.length > 0 ? sectionKeys.join(" · ") : "Empty template"}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => apply(t)}
                  disabled={busyId === t.id}
                  className="bg-brand-navy text-white hover:bg-brand-teal/90"
                  data-testid={`apply-template-${t.id}`}
                >
                  {busyId === t.id ? "Applying…" : "Apply"}
                </Button>
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
